// Sentry init MUST be before React even renders — that way uncaught render
// errors during the very first render still get caught and reported.
import { initSentry, SentryErrorBoundary } from "./sentry";
initSentry();

import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
// Self-hosted fonts (bundled by Vite, precached by the SW) — replaces the
// render-blocking Google Fonts <link>. Variable files cover every weight.
import "@fontsource-variable/inter";
import "@fontsource-variable/jetbrains-mono";
import "@fontsource/zen-dots";
import "./index.css";
import { applyTheme, getThemeSetting, watchSystemTheme } from "./services/theme";

// The inline script in index.html already set the class before first paint;
// this re-applies from the same source of truth (harmless, keeps the two in
// step if the script ever fails) and starts following OS changes for the life
// of the session while the setting is "system".
applyTheme(getThemeSetting());
watchSystemTheme(() => {});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Could not find root element to mount to");

// Minimal fallback — keeps the user out of a white screen while telling
// them something broke. The "Reload" button is the simplest recovery for
// a PWA: tossing the SW-cached bundle is the next escalation step.
//
// Styled with inline `var(--kx-*)` rather than Tailwind classes: this renders
// when React itself has fallen over, so it must not depend on any component
// having mounted. The tokens come from index.css, which is already loaded, and
// the fallbacks after each comma cover the case where even that failed.
function ErrorFallback() {
  return (
    <div style={{
      minHeight: "100vh",
      display: "grid", placeItems: "center",
      background: "rgb(var(--kx-bg, 5 5 5))",
      color: "rgb(var(--kx-ink, 248 250 252))",
      fontFamily: "Inter, sans-serif", padding: 24,
    }}>
      <div style={{ maxWidth: 360, textAlign: "center" }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Something went wrong</h1>
        <p style={{
          fontSize: 13, color: "rgb(var(--kx-t400, 148 163 184))",
          lineHeight: 1.5, marginBottom: 16,
        }}>
          The app hit an error. We've been notified. Reload to try again.
        </p>
        <button onClick={() => window.location.reload()}
          style={{
            padding: "8px 16px", borderRadius: 8,
            background: "rgb(var(--kx-a500, 15 118 110))", color: "#fff", border: "none",
            fontSize: 13, fontWeight: 500, cursor: "pointer",
          }}>
          Reload
        </button>
      </div>
    </div>
  );
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <SentryErrorBoundary fallback={<ErrorFallback />}>
      <App />
    </SentryErrorBoundary>
  </React.StrictMode>,
);
